import React from 'react';
import { Link } from 'react-router-dom';
import { featuresData } from '../data/mockData';
import { ShoppingCart, FileText, Brain, CreditCard, Users, Cloud, ArrowRight, Monitor, Database, Palette, Shield, HardDrive } from 'lucide-react';

const iconMap = {
  ShoppingCart,
  FileText,
  Brain,
  CreditCard,
  Users,
  Cloud
};

const providerIcons = {
  Microsoft: Monitor,
  AWS: Database,
  'Google Cloud': Cloud,
  Adobe: Palette,
  Acronis: Shield,
  Dropbox: HardDrive
};

const providerColors = {
  Microsoft: '#00a4ef',
  AWS: '#ff9900',
  'Google Cloud': '#4285f4',
  Adobe: '#ff0000',
  Acronis: '#1297fd',
  Dropbox: '#0061ff'
};

const Features = () => {
  // Features that have dedicated pages
  const getFeatureLink = (feature) => {
    if (feature.title === "Cloud Marketplace") {
      return "/cloud-marketplace";
    }
    if (feature.title === "Automatización de Contratos") {
      return "/automatizacion-contratos";
    }
    return null; // No link for other features
  };

  return (
    <section id="features" className="py-24 bg-[#e3e4e6]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#1297fd]/10 text-[#1297fd] rounded-full text-sm font-medium mb-4">
            Características
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Diseñado{' '}
            <span className="text-[#1297fd]">pensando en ti</span>
          </h2>
          <p className="text-lg text-[#c3c3c5] max-w-2xl mx-auto">
            Desde contratos hasta cloud, todas tus herramientas bajo una sola plataforma.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuresData.map((feature, idx) => {
            const IconComponent = iconMap[feature.icon];
            const isHighlighted = idx === 0 || idx === 2;
            const featureLink = getFeatureLink(feature);
            const hasLink = featureLink !== null;
            
            return (
              <div
                key={feature.id}
                className={`group relative rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 ${hasLink ? 'cursor-pointer' : ''} ${
                  isHighlighted
                    ? 'text-white shadow-xl shadow-[#1297fd]/20'
                    : 'bg-white border border-[#e3e4e6] hover:border-[#8bcaf9] hover:shadow-xl hover:shadow-[#1297fd]/10'
                }`}
                style={isHighlighted ? { background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' } : {}}
              >
                {/* Icon */}
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 ${
                  isHighlighted
                    ? 'bg-white/20'
                    : 'bg-[#8bcaf9]/20 group-hover:bg-[#8bcaf9]/30'
                }`}>
                  {IconComponent && (
                    <IconComponent className={`w-7 h-7 ${
                      isHighlighted ? 'text-white' : 'text-[#1297fd]'
                    }`} />
                  )}
                </div>

                {/* Content */}
                <h3 className={`text-xl font-bold mb-3 ${
                  isHighlighted ? 'text-white' : 'text-gray-900'
                }`}>
                  {feature.title}
                </h3>
                <p className={`mb-6 leading-relaxed ${
                  isHighlighted ? 'text-white/80' : 'text-[#c3c3c5]'
                }`}>
                  {feature.description}
                </p>

                {/* CTA - Only show for features with pages */}
                {hasLink && (
                  <Link
                    to={featureLink}
                    className={`inline-flex items-center font-medium group/link ${
                      isHighlighted
                        ? 'text-white hover:text-white/80'
                        : 'text-[#1297fd] hover:text-[#40aaf8]'
                    }`}
                  >
                    Más información
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                )}

                {/* Decorative Element */}
                <div className={`absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-10 ${
                  isHighlighted ? 'bg-white' : 'bg-[#1297fd]'
                }`} />
              </div>
            );
          })}
        </div>

        {/* Integration Logos */}
        <div className="mt-20">
          <p className="text-center text-[#c3c3c5] mb-8">Integración perfecta con los principales proveedores</p>
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
            {['Microsoft', 'AWS', 'Google Cloud', 'Adobe', 'Acronis', 'Dropbox'].map((partner) => {
              const IconComponent = providerIcons[partner];
              const color = providerColors[partner];
              return (
                <div
                  key={partner}
                  className="group px-5 py-4 bg-white rounded-xl shadow-sm border border-[#e3e4e6] hover:shadow-lg hover:border-[#8bcaf9] transition-all hover:scale-105 flex items-center gap-3 cursor-pointer"
                >
                  <div 
                    className="h-9 w-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    {IconComponent && <IconComponent className="h-5 w-5" style={{ color }} />}
                  </div>
                  <span className="text-gray-700 font-medium group-hover:text-[#1297fd] transition-colors">{partner}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
